import { useMemo, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText,
  DialogTitle, IconButton, ListItemText, Menu, MenuItem, Paper, Switch, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import api from '../services/api';
import { safeDecode } from '../utils/decode';
import {
  headCellSx, numeric, pairCellSx, pairRowSx, ProgressBar, StatusChip, tableContainerSx,
} from '../quotas/QuotaTable';
import { BULK_ACTIONS, BulkBar, BulkDialog } from './BulkActions';
import { fieldErrors, wranglerError } from './errors';
import ImportDialog from './ImportDialog';
import ImportResultView from './ImportResultView';
import { OverallQuotaBox, OverallQuotaDialog } from './OverallQuota';
import QuotaEditor from './QuotaEditor';
import QuotaSetBuilder from './QuotaSetBuilder';
import { readKept, writeKept } from './skips';
import SpreadsheetDialog from './SpreadsheetDialog';
import { useAlchemerCounts, useImports, useWranglerQuestions, useWranglerQuotas } from './useWranglerData';
import reviewByQuota from './importReview';

const FILTERS = {
  all: { label: 'All', test: () => true },
  closed: { label: 'Closed', test: (q) => q.closed },
  review: { label: 'Needs review', test: (q) => q.needs_review },
  off: { label: 'OFF', test: (q) => !q.is_on },
};

const NO_SET = '\u0000none';  // the Set filter's "Not in a set" (no set name can be this)

const CREATE_OPTIONS = [
  { id: 'single', label: 'Single quota', available: true },
  { id: 'set', label: 'Quota set', available: true },
  { id: 'overall', label: 'Overall quota', available: true },
  { id: 'import', label: 'Import from Alchemer', available: true },
  { id: 'spreadsheet', label: 'Upload spreadsheet', available: true },
];

// A new quota starts OFF, so it never blocks respondents before it's been checked.
const NEW_QUOTA = { name: '', description: '', logic: null, original_limit: '', is_on: false, set_name: '' };

const dismissKey = (surveyId, jobId) => `wrangler-not-converted-dismissed-${surveyId}-${jobId}`;
const readDismissed = (key) => {
  try { return window.localStorage.getItem(key) === '1'; } catch { return false; }
};

// The shared bar and chips speak responses / limit / reserved.
const asProgress = (q) => ({ ...q, responses: q.complete_count, limit: q.current_limit, reserved: q.reserved_count });

const LimitCell = ({ quota }) => {
  const change = quota.current_limit - quota.original_limit;
  return (
    <Box component="span" sx={{ whiteSpace: 'nowrap' }}>
      {quota.complete_count} / {quota.current_limit}
      {change !== 0 && (
        <Tooltip title={`Original limit ${quota.original_limit}`}>
          <Box component="span" sx={{ color: 'text.secondary', ml: 0.5 }}>({change > 0 ? '+' : '−'}{Math.abs(change)})</Box>
        </Tooltip>
      )}
    </Box>
  );
};

/** The Wrangler page's Quotas tab: the list (design §2), import (§6) and the review flow. */
const QuotasTab = ({ survey }) => {
  const surveyId = survey.id;
  const { quotas, loading, error, reload, replaceQuota, setData } = useWranglerQuotas(surveyId);
  const { questions } = useWranglerQuestions(surveyId);
  const imports = useImports(surveyId);
  const alchemerCounts = useAlchemerCounts(surveyId);

  const [filter, setFilter] = useState('all');
  // '' for all sets, NO_SET for quotas not in a set, or a set name. Combines with the status filter and search.
  const [quotaSetFilter, setQuotaSetFilter] = useState('');
  const [search, setSearch] = useState('');
  // The editor: {mode: 'edit', id} for a saved quota, {mode: 'create', draft} for a new one or a duplicate.
  const [editor, setEditor] = useState(null);
  const openId = editor?.mode === 'edit' ? editor.id : null;
  const setOpenId = (id) => setEditor(id == null ? null : { mode: 'edit', id });
  const [deleting, setDeleting] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [bulkCreate, setBulkCreate] = useState(null); // 'set' | 'spreadsheet' | 'overall'
  const [selected, setSelected] = useState(() => new Set());
  const [bulkKind, setBulkKind] = useState(null);
  const [actionMessage, setActionMessage] = useState(null);
  const [resultOpen, setResultOpen] = useState(false);
  const [pending, setPending] = useState({});
  const [confirmOn, setConfirmOn] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [, setDismissTick] = useState(0);

  // The overall quota has its own box above the list (design §8); everything else is the list.
  const overall = quotas.find((q) => q.is_overall) || null;
  const listed = useMemo(() => quotas.filter((q) => !q.is_overall), [quotas]);
  const shown = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const inSet = (q) => (quotaSetFilter === '' ? true : quotaSetFilter === NO_SET ? !q.set_name : q.set_name === quotaSetFilter);
    return listed.filter((q) => FILTERS[filter].test(q) && inSet(q) && (!needle
      || safeDecode(q.name).toLowerCase().includes(needle) || (q.description || '').toLowerCase().includes(needle)));
  }, [listed, filter, quotaSetFilter, search]);

  const counts = {
    total: listed.length,
    on: listed.filter((q) => q.is_on).length,
    closed: listed.filter((q) => q.closed).length,
    review: listed.filter((q) => q.needs_review).length,
  };

  // Ticked quotas, for bulk actions. Ticks survive filtering; the bar says how many are hidden.
  const ticked = listed.filter((q) => selected.has(q.id));
  const shownIds = new Set(shown.map((q) => q.id));
  const hiddenTicked = ticked.filter((q) => !shownIds.has(q.id)).length;
  const allShownTicked = shown.length > 0 && shown.every((q) => selected.has(q.id));
  const toggleTick = (id) => setSelected((s) => {
    const next = new Set(s);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const tickShown = () => setSelected((s) => {
    const next = new Set(s);
    shown.forEach((q) => (allShownTicked ? next.delete(q.id) : next.add(q.id)));
    return next;
  });
  const tickAllLabel = `${allShownTicked ? 'Untick' : 'Tick'} all ${shown.length} shown`;
  const bulkApplied = (kind, data, { reload: needsReload } = {}) => {
    setBulkKind(null);
    setSelected(new Set());
    if (needsReload) {
      reload();
      if (data?.changed) setActionMessage(BULK_ACTIONS[kind].done(data.changed.length));
      return;
    }
    if (kind === 'delete') {
      const gone = new Set(data.deleted);
      setData((list) => list.filter((q) => !gone.has(q.id)));
      setActionMessage(BULK_ACTIONS.delete.done(data.deleted.length));
    } else {
      const updated = new Map(data.quotas.map((q) => [q.id, q]));
      setData((list) => list.map((q) => updated.get(q.id) || q));
      setActionMessage(BULK_ACTIONS[kind].done(data.quotas.length));
    }
  };

  // Every import re-reports the quotas it couldn't copy, so the banner and "See which" use the newest.
  const latestImport = imports.jobs[0] ?? null;
  const importResult = latestImport?.progress?.result;
  const notConverted = importResult?.not_converted || [];
  const bannerKey = latestImport ? dismissKey(surveyId, latestImport.id) : null;
  const showNotConverted = notConverted.length > 0 && bannerKey && !readDismissed(bannerKey);
  // Each quota's review details come from the import that created it, so a later import changes none of them.
  const review = reviewByQuota(imports.jobs);
  const keepSkips = (quotaId) => { writeKept(surveyId, review.get(quotaId)?.jobId, quotaId); setDismissTick((n) => n + 1); };

  const setOn = async (quota, isOn) => {
    setPending((p) => ({ ...p, [quota.id]: true }));
    setActionError(null);
    try {
      const { data } = await api.patch(`/surveys/${surveyId}/wrangler/quotas/${quota.id}/`, { is_on: isOn });
      replaceQuota(data.quota);
    } catch (err) {
      setActionError([`${safeDecode(quota.name)}: ${wranglerError(err)}`, ...fieldErrors(err)].join('\n'));
    } finally {
      setPending((p) => ({ ...p, [quota.id]: false }));
    }
  };

  // Switching ON a copy of an Alchemer quota: both now block respondents, so say so first.
  const toggle = (quota, isOn) => {
    if (isOn && quota.alchemer_quota_id != null) setConfirmOn(quota);
    else setOn(quota, isOn);
  };

  const setNames = useMemo(() => [...new Set(quotas.map((q) => q.set_name).filter(Boolean))].sort(), [quotas]);
  const open = quotas.find((q) => q.id === openId) || null;
  const reviewQueue = filter === 'review' ? shown.filter((q) => q.needs_review && q.id !== openId) : [];

  const createMenu = (option) => {
    setMenuAnchor(null);
    if (option === 'import') setImportOpen(true);
    if (option === 'single') setEditor({ mode: 'create', draft: NEW_QUOTA, key: Date.now() });
    if (option === 'set' || option === 'spreadsheet' || option === 'overall') setBulkCreate(option);
  };

  // Duplicate (decision 5): the editor opens prefilled and OFF; nothing exists until Create.
  const duplicate = (quota) => setEditor({
    mode: 'create',
    key: Date.now(),
    draft: {
      name: `Copy of ${safeDecode(quota.name)}`, description: quota.description || '', logic: quota.logic,
      original_limit: quota.current_limit, is_on: false, set_name: quota.set_name || '',
    },
  });

  const saved = (quota, { created } = {}) => {
    if (created) setData((list) => [...list, quota]);
    else replaceQuota(quota);
  };
  const removed = (id) => setData((list) => list.filter((q) => q.id !== id));
  const createdMany = (created) => setData((list) => [...list, ...created]);

  const confirmDelete = async () => {
    const quota = deleting;
    setPending((p) => ({ ...p, [quota.id]: true }));
    setActionError(null);
    try {
      await api.delete(`/surveys/${surveyId}/wrangler/quotas/${quota.id}/`);
      removed(quota.id);
      setDeleting(null);
    } catch (err) {
      setActionError(`${safeDecode(quota.name)}: ${wranglerError(err)}`);
      setDeleting(null);
    } finally {
      setPending((p) => ({ ...p, [quota.id]: false }));
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 2 }}>
        <Button variant="contained" endIcon={<ArrowDropDownIcon />} onClick={(e) => setMenuAnchor(e.currentTarget)}
          sx={{ textTransform: 'none', borderRadius: 2 }}>
          Create quota
        </Button>
        <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
          {CREATE_OPTIONS.map((o) => (
            <MenuItem key={o.id} disabled={!o.available || (o.id === 'overall' && Boolean(overall))} onClick={() => createMenu(o.id)}>
              <ListItemText primary={o.label}
                secondary={!o.available ? 'Not available yet' : o.id === 'overall' && overall ? 'This survey has one' : null} />
            </MenuItem>
          ))}
        </Menu>
        <TextField size="small" label="Search name or description" value={search} onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 260 }} />
        <ToggleButtonGroup size="small" exclusive value={filter} onChange={(e, v) => v && setFilter(v)} aria-label="Show">
          {Object.entries(FILTERS).map(([id, f]) => (
            <ToggleButton key={id} value={id} sx={{ textTransform: 'none', px: 1.5 }}>{f.label}</ToggleButton>
          ))}
        </ToggleButtonGroup>
        {setNames.length > 0 && (
          <TextField select size="small" label="Set" value={quotaSetFilter} onChange={(e) => setQuotaSetFilter(e.target.value)}
            SelectProps={{ displayEmpty: true }} InputLabelProps={{ shrink: true }} sx={{ minWidth: 200 }}>
            <MenuItem value="">All sets</MenuItem>
            {setNames.map((name) => (
              <MenuItem key={name} value={name}>{name} ({listed.filter((q) => q.set_name === name).length})</MenuItem>
            ))}
            <MenuItem value={NO_SET}>Not in a set ({listed.filter((q) => !q.set_name).length})</MenuItem>
          </TextField>
        )}
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
          {counts.total} quota{counts.total !== 1 ? 's' : ''} · {counts.on} ON · {counts.closed} closed · {counts.review} need review
          {shown.length !== listed.length && ` · showing ${shown.length}`}
        </Typography>
        {listed.length > 0 && (
          <Button size="small" onClick={tickShown} disabled={!shown.length} sx={{ textTransform: 'none' }}>
            {tickAllLabel}
          </Button>
        )}
      </Box>

      {overall && (
        <OverallQuotaBox surveyId={surveyId} quota={overall} onChanged={replaceQuota} onReload={reload} onDeleted={removed} />
      )}
      {ticked.length > 0 && (
        <BulkBar count={ticked.length} hidden={hiddenTicked} onAction={setBulkKind} onClear={() => setSelected(new Set())} />
      )}
      {actionMessage && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setActionMessage(null)}>{actionMessage}</Alert>}

      {showNotConverted && (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          action={(
            <>
              <Button color="inherit" size="small" onClick={() => setResultOpen(true)} sx={{ textTransform: 'none' }}>See which</Button>
              <Button color="inherit" size="small" sx={{ textTransform: 'none' }}
                onClick={() => { try { window.localStorage.setItem(bannerKey, '1'); } catch { /* not kept */ } setDismissTick((n) => n + 1); }}>
                Dismiss
              </Button>
            </>
          )}
        >
          {notConverted.length} Alchemer quota{notConverted.length !== 1 ? 's' : ''} couldn&apos;t be copied and need building by hand.
        </Alert>
      )}
      {error && <Alert severity="error" sx={{ mb: 2 }} action={<Button color="inherit" size="small" onClick={reload}>Try again</Button>}>{error}</Alert>}
      {actionError && <Alert severity="error" sx={{ mb: 2, whiteSpace: 'pre-line' }} onClose={() => setActionError(null)}>{actionError}</Alert>}

      {loading && !quotas.length ? <CircularProgress size={24} /> : listed.length === 0 && !error ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
          <Typography sx={{ mb: 1 }}>This survey has no Wrangler quotas yet.</Typography>
          <Button variant="outlined" onClick={() => setImportOpen(true)} sx={{ textTransform: 'none' }}>Import from Alchemer</Button>
        </Paper>
      ) : listed.length > 0 && (
        <TableContainer component={Paper} elevation={0} sx={tableContainerSx}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                {[
                  { id: 'tick', label: '', width: 48 },
                  { id: 'name', label: 'Name', minWidth: 140 },
                  { id: 'on', label: 'ON/OFF' },
                  { id: 'limit', label: 'Completes / Limit', numeric: true },
                  { id: 'held', label: 'Holding a place', numeric: true },
                  { id: 'fill', label: 'Fill', minWidth: 170 },
                  { id: 'status', label: 'Status' },
                  { id: 'actions', label: '', width: 88 },
                ].map((col, i, cols) => (
                  <TableCell key={col.id} sx={headCellSx(col, i, cols.length)}>
                    {col.id === 'tick' ? (
                      <Tooltip title={tickAllLabel}>
                        <span>
                          <Checkbox size="small" checked={allShownTicked} disabled={!shown.length}
                            indeterminate={!allShownTicked && shown.some((q) => selected.has(q.id))} onChange={tickShown}
                            inputProps={{ 'aria-label': tickAllLabel }} />
                        </span>
                      </Tooltip>
                    ) : col.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {shown.length === 0 && (
                <TableRow><TableCell colSpan={8} sx={{ py: 4, textAlign: 'center', color: 'text.secondary' }}>No quotas match.</TableCell></TableRow>
              )}
              {shown.map((quota, i) => {
                const cell = pairCellSx(true);
                const cantSwitchOn = quota.group_join_unknown && !quota.is_on;
                return (
                  <TableRow key={quota.id} onClick={() => setOpenId(quota.id)}
                    sx={{ ...pairRowSx(i, i === shown.length - 1), cursor: 'pointer' }}>
                    <TableCell sx={cell} onClick={(e) => e.stopPropagation()}>
                      <Checkbox size="small" checked={selected.has(quota.id)} onChange={() => toggleTick(quota.id)}
                        inputProps={{ 'aria-label': `Tick ${safeDecode(quota.name)}` }} />
                    </TableCell>
                    <TableCell sx={cell}>
                      <Typography variant="body2" fontWeight={500}>{safeDecode(quota.name)}</Typography>
                      {quota.description && <Typography variant="caption" color="text.secondary" display="block">{quota.description}</Typography>}
                      {quota.set_name && <Typography variant="caption" color="text.secondary" display="block">Set: {quota.set_name}</Typography>}
                    </TableCell>
                    <TableCell sx={cell} onClick={(e) => e.stopPropagation()}>
                      <Tooltip title={cantSwitchOn ? 'Choose AND or OR first: open the quota' : quota.is_on ? 'Switch OFF' : 'Switch ON'}>
                        <span>
                          <Switch size="small" checked={quota.is_on} disabled={Boolean(pending[quota.id]) || cantSwitchOn}
                            onChange={(e) => toggle(quota, e.target.checked)}
                            inputProps={{ 'aria-label': `${safeDecode(quota.name)} ON` }} />
                        </span>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ ...cell, ...numeric }}><LimitCell quota={quota} /></TableCell>
                    <TableCell sx={{ ...cell, ...numeric }}>{quota.reserved_count}</TableCell>
                    <TableCell sx={cell}><ProgressBar quota={asProgress(quota)} /></TableCell>
                    <TableCell sx={cell}>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'nowrap' }}>
                        {quota.closed && <StatusChip label="Closed" colour="error" title="Full, counting places held" />}
                        {quota.needs_review && <StatusChip label="Needs review" colour="warning" title="Imported from Alchemer and not reviewed yet" />}
                        {quota.group_join_unknown && <StatusChip label="AND/OR needed" colour="warning" title="Choose how its groups combine before switching it on" />}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ ...cell, whiteSpace: 'nowrap' }} onClick={(e) => e.stopPropagation()}>
                      <Tooltip title="Duplicate">
                        <IconButton size="small" aria-label={`Duplicate ${safeDecode(quota.name)}`} onClick={() => duplicate(quota)}>
                          <ContentCopyRoundedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <span>
                          <IconButton size="small" aria-label={`Delete ${safeDecode(quota.name)}`} disabled={Boolean(pending[quota.id])}
                            onClick={() => setDeleting(quota)}>
                            <DeleteOutlineRoundedIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {open && (
        <QuotaEditor
          key={`edit-${open.id}`}
          surveyId={surveyId}
          mode="edit"
          quota={open}
          questions={questions}
          // v1.5.0: count_check null means its import couldn't read Alchemer's completes: counts not checked.
          alchemerCount={open.alchemer_quota_id != null && review.get(open.id)?.countCheck !== null
            ? alchemerCounts[open.alchemer_quota_id] : null}
          joinChoice={review.get(open.id)?.joinChoice}
          skipFlag={review.get(open.id)?.skipFlag}
          skipsKept={review.get(open.id)?.skipFlag ? readKept(surveyId, review.get(open.id).jobId, open.id) : false}
          onKeepSkips={() => keepSkips(open.id)}
          setNames={setNames}
          onDuplicate={duplicate}
          onSaved={saved}
          onDeleted={removed}
          onReload={reload}
          onClose={() => setEditor(null)}
          onNext={reviewQueue.length ? () => setOpenId(reviewQueue[0].id) : null}
          nextLabel={`Next to review (${reviewQueue.length} left)`}
        />
      )}
      {editor?.mode === 'create' && (
        <QuotaEditor
          key={`create-${editor.key}`}
          surveyId={surveyId}
          mode="create"
          quota={editor.draft}
          questions={questions}
          setNames={setNames}
          onSaved={saved}
          onDeleted={removed}
          onReload={reload}
          onClose={() => setEditor(null)}
        />
      )}

      <Dialog open={Boolean(deleting)} onClose={() => setDeleting(null)}>
        <DialogTitle>Delete {deleting && safeDecode(deleting.name)}?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Its limit history goes with it, and quota views and trackers that include it lose it. Respondents it would
            have blocked are let through. This can&apos;t be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleting(null)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button color="error" variant="contained" onClick={confirmDelete} sx={{ textTransform: 'none' }}>Delete</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(confirmOn)} onClose={() => setConfirmOn(null)}>
        <DialogTitle>Switch ON {confirmOn && safeDecode(confirmOn.name)}?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This is a copy of an Alchemer quota, and the Alchemer original is still active, so both will now block
            respondents. The copy may close slightly earlier, because it also counts people holding a place.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOn(null)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" sx={{ textTransform: 'none' }}
            onClick={() => { setOn(confirmOn, true); setConfirmOn(null); }}>
            Switch ON
          </Button>
        </DialogActions>
      </Dialog>

      {bulkCreate === 'set' && (
        <QuotaSetBuilder surveyId={surveyId} questions={questions} existingSets={setNames} onCreated={createdMany}
          onClose={() => setBulkCreate(null)} />
      )}
      {bulkCreate === 'overall' && (
        <OverallQuotaDialog surveyId={surveyId} onCreated={(q) => createdMany([q])} onClose={() => setBulkCreate(null)} />
      )}
      {bulkKind && (
        <BulkDialog surveyId={surveyId} kind={bulkKind} quotas={ticked} onClose={() => setBulkKind(null)} onApplied={bulkApplied} />
      )}
      {bulkCreate === 'spreadsheet' && (
        <SpreadsheetDialog surveyId={surveyId} questions={questions} onCreated={createdMany} onClose={() => setBulkCreate(null)} />
      )}
      <ImportDialog
        surveyId={surveyId}
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onFinished={() => { reload(); imports.reload(); }}
      />
      <Dialog open={resultOpen} onClose={() => setResultOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Last import from Alchemer</DialogTitle>
        <DialogContent><ImportResultView result={importResult} /></DialogContent>
        <DialogActions><Button onClick={() => setResultOpen(false)} sx={{ textTransform: 'none' }}>Close</Button></DialogActions>
      </Dialog>
    </Box>
  );
};

export default QuotasTab;
